import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT, NAV } from "@/content/site";

export const Logo = ({ testId }) => (
  <picture>
    <source type="image/webp" srcSet="/img/logo-160.webp 1x, /img/logo-320.webp 2x" />
    <img src="/img/logo-160.png" alt="VidaVital Medical" width="160" height="93" className="h-10 w-auto md:h-11" data-testid={testId} />
  </picture>
);

const linkCls = ({ isActive }) =>
  `relative inline-flex min-h-[44px] items-center font-display text-[0.8125rem] font-medium uppercase tracking-[0.1em] transition-colors hover:text-gold ${isActive ? "text-gold" : "text-ivory"}`;

export const Header = () => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); btnRef.current?.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="on-dark sticky top-0 z-50 border-b border-gold/25 bg-ink text-ivory" data-testid="site-header">
      <div className="wrap flex h-16 items-center justify-between gap-4 md:h-[72px]">
        <Link to="/" className="shrink-0" aria-label="VidaVital Medical Franchise home" data-testid="header-logo-link"><Logo /></Link>
        <nav aria-label="Primary" className="hidden lg:block">
          <ul className="flex items-center gap-8">
            {NAV.map((n) => (
              <li key={n.to}><NavLink to={n.to} className={linkCls} data-testid={`nav-link-${n.to.slice(1)}`}>{n.label}</NavLink></li>
            ))}
          </ul>
        </nav>
        <div className="flex items-center gap-2 sm:gap-4">
          <a href={CONTACT.tel} data-loc="header" className="hidden min-h-[44px] items-center gap-2 text-sm font-semibold text-ivory hover:text-gold xl:inline-flex" data-testid="header-phone-link">
            <Phone aria-hidden="true" className="h-4 w-4 text-gold" />{CONTACT.phone}
          </a>
          <Link to="/contact" data-cta="header-explore-ownership" className="btn-gold hidden min-h-[44px] px-4 text-xs min-[360px]:inline-flex sm:px-5" data-testid="nav-explore-cta">Explore Ownership</Link>
          <button
            ref={btnRef}
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center text-ivory hover:text-gold lg:hidden"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X aria-hidden="true" className="h-6 w-6" /> : <Menu aria-hidden="true" className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <nav id="mobile-menu" aria-label="Mobile" className="border-t border-gold/20 bg-ink lg:hidden" data-testid="mobile-menu">
          <ul className="wrap divide-y divide-ivory/10 py-2">
            {[{ to: "/", label: "Overview" }, ...NAV].map((n) => (
              <li key={n.to}><NavLink to={n.to} end className={(s) => `${linkCls(s)} flex min-h-[52px] w-full text-sm`} data-testid={`mobile-nav-link-${n.to.slice(1) || "home"}`}>{n.label}</NavLink></li>
            ))}
            <li className="flex flex-col gap-1 py-4 text-sm">
              <a href={CONTACT.tel} data-loc="mobile-menu" className="inline-flex min-h-[44px] items-center text-ivory" data-testid="mobile-menu-phone">Call {CONTACT.phone}</a>
              <a href={CONTACT.mailto} data-loc="mobile-menu" className="inline-flex min-h-[44px] items-center break-all text-ivory" data-testid="mobile-menu-email">{CONTACT.email}</a>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
};
